import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDownUp, ChevronDown, ChevronRight, ChevronUp, Download, MoreHorizontal, Rows3, Search,
} from "lucide-react";
import BulkBar from "./BulkBar";
import ColumnPicker from "./ColumnPicker";
import DetailDrawer from "./DetailDrawer";
import Filters from "./Filters";
import Pagination from "./Pagination";
import Popover from "./Popover";
import SavedViews from "./SavedViews";
import { Empty, ErrorState, NotApplicable, PermissionDenied } from "./States";

/* ────────────────────────────────────────────────────────────────────────────
   Tenue en charge : pagination stricte, fenêtrage en renfort.

   Choix retenu : la pagination est stricte. Une page ne dépasse jamais
   MAX_PAGE_SIZE lignes, y compris quand l'appelant demande plus, et le
   sélecteur de taille de page s'arrête là. Raisons : le coût réel d'un grand
   tableau d'administration n'est pas le rendu mais le transfert et la
   sérialisation côté API ; la pagination garde les URL partageables, l'export
   prévisible et l'impression possible ; et une liste virtualisée casse la
   recherche native du navigateur (⌘F), ce qui compte pour un outil d'enquête.

   Le fenêtrage reste disponible pour les listes volontairement non paginées
   (pageSize={0}) : au-delà de WINDOW_THRESHOLD lignes rendues, seules les
   lignes visibles sont montées, encadrées par deux <tr> tampons. Il exige une
   hauteur de ligne fixe — c'est pourquoi les cellules sont sur une seule ligne,
   tronquées à l'ellipse, quelle que soit la densité.
   ──────────────────────────────────────────────────────────────────────────── */
const MAX_PAGE_SIZE = 100;
const WINDOW_THRESHOLD = 200;
const ROW_HEIGHT = { compact: 34, cosy: 44 };
const OVERSCAN = 8;
const DEFAULT_WIDTH = 170;

const storeKey = id => `perform.admin.table.${id}`;

function readPrefs(id) {
  try {
    const raw = window.localStorage.getItem(storeKey(id));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writePrefs(id, prefs) {
  try {
    window.localStorage.setItem(storeKey(id), JSON.stringify(prefs));
  } catch {
    /* Stockage indisponible : les préférences restent en mémoire. */
  }
}

const rawValue = (col, row) => (col.value ? col.value(row) : row[col.key]);

function compare(a, b) {
  if (a === b) return 0;
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "fr", { numeric: true, sensitivity: "base" });
}

function matchesFilter(def, value, row) {
  if (def.match) return def.match(row, value);
  const cell = row[def.key];
  switch (def.type) {
    case "multi": return !value.length || value.includes(cell);
    case "bool": return String(cell) === String(value);
    case "date": return String(cell ?? "").startsWith(value);
    case "text": return String(cell ?? "").toLowerCase().includes(String(value).toLowerCase());
    default: return String(cell) === String(value);
  }
}

function toCsv(columns, rows) {
  const cell = v => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = columns.map(c => cell(c.header)).join(";");
  const body = rows.map(row => columns.map(c => cell(c.exportValue ? c.exportValue(row) : rawValue(c, row))).join(";"));
  // BOM + point-virgule : Excel en français ouvre le fichier sans étape de conversion.
  return `﻿${[head, ...body].join("\r\n")}`;
}

function download(name, content, type) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function SkeletonRows({ rows = 8, cols = 5 }) {
  return (
    <div className="adm-dt-skel" style={{ "--adm-skel-cols": cols }} aria-hidden="true" data-testid="table-skeleton">
      {Array.from({ length: rows }, (_, r) => (
        <div className="adm-dt-skel-row" key={r}>
          <span className="adm-sk" style={{ width: 14, height: 14, borderRadius: 4 }} />
          {Array.from({ length: cols }, (_, c) => (
            <span className="adm-sk" key={c} style={{ width: `${[92, 64, 78, 50, 70][(r + c) % 5]}%` }} />
          ))}
        </div>
      ))}
    </div>
  );
}

export default function DataTable({
  id,
  columns,
  rows = [],
  getRowId = row => row.id,
  total,
  serverSide = false,
  loading = false,
  error = null,
  onRetry,
  denied = false,
  requiredRole,
  permission,
  notApplicable = null,
  searchable = true,
  searchPlaceholder = "Rechercher…",
  filters = [],
  paginationMode = "offset",
  cursor = null,
  onCursorChange,
  pageSizes = [25, 50, 100],
  initialPageSize = 50,
  selectable = false,
  bulkActions = [],
  rowActions,
  onRowClick,
  renderDetail,
  detailTitle,
  detailSubtitle,
  detailTabs,
  onQueryChange,
  emptyTitle,
  emptyText,
  emptyAction,
  exportName,
  density: densityProp,
  builtinViews = [],
  toolbarExtra,
  maxHeight,
  searchInputRef,
}) {
  const prefs = useMemo(() => readPrefs(id) || {}, [id]);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [filterValues, setFilterValues] = useState({});
  const [sort, setSort] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(Math.min(initialPageSize, MAX_PAGE_SIZE));
  const [density, setDensity] = useState(densityProp || prefs.density || "compact");
  const [widths, setWidths] = useState(prefs.widths || {});
  const [visible, setVisible] = useState(
    prefs.columns?.filter(key => columns.some(c => c.key === key)) || columns.filter(c => !c.hidden).map(c => c.key),
  );
  const [selected, setSelected] = useState(() => new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [openRow, setOpenRow] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [windowTop, setWindowTop] = useState(0);
  const [viewport, setViewport] = useState(600);

  const scroller = useRef(null);
  const lastIndex = useRef(null);
  const queryRef = useRef(onQueryChange);
  queryRef.current = onQueryChange;

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 180);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    writePrefs(id, { columns: visible, widths, density });
  }, [id, visible, widths, density]);

  const filterKey = JSON.stringify(filterValues);
  useEffect(() => { setPage(1); }, [debounced, filterKey, pageSize]);

  const query = useMemo(() => ({
    search: debounced,
    filters: filterValues,
    sort,
    page,
    pageSize,
    cursor: paginationMode === "cursor" ? cursor : null,
  }), [debounced, filterValues, sort, page, pageSize, paginationMode, cursor]);

  useEffect(() => { queryRef.current?.(query); }, [query]);

  /* ---------- Traitement côté client (mode démo / petites listes) ---------- */
  const processed = useMemo(() => {
    if (serverSide) return rows;
    const activeFilters = filters.filter(def => {
      const v = filterValues[def.key];
      return v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && !v.length);
    });
    const needle = debounced.trim().toLowerCase();
    const shownCols = visible.map(key => columns.find(c => c.key === key)).filter(Boolean);

    let out = rows.filter(row => {
      if (!activeFilters.every(def => matchesFilter(def, filterValues[def.key], row))) return false;
      if (!needle) return true;
      return shownCols.some(col => String(rawValue(col, row) ?? "").toLowerCase().includes(needle));
    });

    if (sort) {
      const col = columns.find(c => c.key === sort.key);
      if (col) {
        out = [...out].sort((a, b) => compare(rawValue(col, a), rawValue(col, b)) * (sort.dir === "desc" ? -1 : 1));
      }
    }
    return out;
  }, [serverSide, rows, filters, filterValues, debounced, visible, columns, sort]);

  const count = serverSide ? (total ?? rows.length) : processed.length;
  const paged = useMemo(() => {
    if (serverSide || pageSize === 0) return processed;
    const start = (page - 1) * pageSize;
    return processed.slice(start, start + pageSize);
  }, [serverSide, processed, page, pageSize]);

  /* ---------- Fenêtrage (listes non paginées uniquement) ---------- */
  const rowHeight = ROW_HEIGHT[density] || ROW_HEIGHT.compact;
  const windowed = pageSize === 0 && paged.length > WINDOW_THRESHOLD;
  const firstIndex = windowed ? Math.max(0, Math.floor(windowTop / rowHeight) - OVERSCAN) : 0;
  const lastRendered = windowed
    ? Math.min(paged.length, Math.ceil((windowTop + viewport) / rowHeight) + OVERSCAN)
    : paged.length;
  const rendered = windowed ? paged.slice(firstIndex, lastRendered) : paged;

  useEffect(() => {
    if (!windowed || !scroller.current) return undefined;
    const node = scroller.current;
    const measure = () => setViewport(node.clientHeight || 600);
    measure();
    const onScroll = () => setWindowTop(node.scrollTop);
    node.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", measure);
    return () => { node.removeEventListener("scroll", onScroll); window.removeEventListener("resize", measure); };
  }, [windowed]);

  /* ---------- Colonnes ---------- */
  const shownColumns = useMemo(
    () => visible.map(key => columns.find(c => c.key === key)).filter(Boolean),
    [visible, columns],
  );

  const startResize = (col, event) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = widths[col.key] || col.width || DEFAULT_WIDTH;
    const min = col.minWidth || 72;
    setResizing(col.key);
    const move = e => setWidths(w => ({ ...w, [col.key]: Math.max(min, Math.round(startWidth + e.clientX - startX)) }));
    const stop = () => {
      setResizing(null);
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", stop);
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", stop);
  };

  const toggleSort = col => {
    if (!col.sortable) return;
    setSort(current => {
      if (!current || current.key !== col.key) return { key: col.key, dir: "asc" };
      if (current.dir === "asc") return { key: col.key, dir: "desc" };
      return null;
    });
  };

  /* ---------- Sélection ---------- */
  const pageIds = useMemo(() => paged.map(getRowId), [paged, getRowId]);
  const allPageSelected = pageIds.length > 0 && pageIds.every(rowId => selected.has(rowId));
  const someSelected = selected.size > 0;

  const clearSelection = useCallback(() => { setSelected(new Set()); setAllMatching(false); lastIndex.current = null; }, []);

  const toggleRow = (rowId, index, event) => {
    setAllMatching(false);
    setSelected(current => {
      const next = new Set(current);
      // Maj+clic étend la sélection depuis la dernière ligne cochée.
      if (event?.shiftKey && lastIndex.current !== null) {
        const [from, to] = [lastIndex.current, index].sort((a, b) => a - b);
        const add = !next.has(rowId);
        for (let i = from; i <= to; i += 1) {
          if (add) next.add(pageIds[i]); else next.delete(pageIds[i]);
        }
      } else if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      lastIndex.current = index;
      return next;
    });
  };

  const toggleAllOnPage = () => {
    setAllMatching(false);
    setSelected(current => {
      const next = new Set(current);
      if (allPageSelected) pageIds.forEach(rowId => next.delete(rowId));
      else pageIds.forEach(rowId => next.add(rowId));
      return next;
    });
  };

  useEffect(() => { clearSelection(); }, [debounced, filterKey, clearSelection]);

  /* ---------- Export ---------- */
  const exportRows = () => (someSelected && !allMatching ? paged.filter(r => selected.has(getRowId(r))) : processed);
  const baseName = exportName || id;
  const exportCsv = close => {
    download(`${baseName}-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(shownColumns, exportRows()), "text/csv;charset=utf-8");
    close();
  };
  const exportJson = close => {
    const data = exportRows().map(row => Object.fromEntries(shownColumns.map(c => [c.key, c.exportValue ? c.exportValue(row) : rawValue(c, row)])));
    download(`${baseName}-${new Date().toISOString().slice(0, 10)}.json`, JSON.stringify(data, null, 2), "application/json");
    close();
  };

  /* ---------- Vues enregistrées ---------- */
  const currentView = { search, filters: filterValues, sort, density, columns: visible };
  const applyView = view => {
    setSearch(view.search ?? "");
    setFilterValues(view.filters ?? {});
    setSort(view.sort ?? null);
    if (view.density) setDensity(view.density);
    if (view.columns?.length) setVisible(view.columns.filter(key => columns.some(c => c.key === key)));
    setPage(1);
  };

  const openDetail = row => {
    if (renderDetail) setOpenRow(row);
    onRowClick?.(row);
  };

  /* ---------- Rendu ---------- */
  if (denied) {
    return (
      <div className="adm-dt" data-testid={`table-${id}`}>
        <PermissionDenied requiredRole={requiredRole} permission={permission} inline={false} />
      </div>
    );
  }
  if (notApplicable) {
    return (
      <div className="adm-dt" data-testid={`table-${id}`}>
        <NotApplicable title={notApplicable.title} text={notApplicable.text} action={notApplicable.action} inline={false} />
      </div>
    );
  }

  const colWidth = col => widths[col.key] || col.width || DEFAULT_WIDTH;
  const body = () => {
    if (loading) return <SkeletonRows rows={Math.min(pageSize || 8, 8)} cols={Math.min(shownColumns.length, 6)} />;
    if (error) return <div className="adm-dt-state"><ErrorState error={error} onRetry={onRetry} /></div>;
    if (!paged.length) {
      return (
        <div className="adm-dt-state">
          <Empty
            title={emptyTitle || "Aucun résultat"}
            text={emptyText || (debounced || Object.keys(filterValues).length
              ? "Aucun élément ne correspond à cette recherche. Élargissez les filtres."
              : "Cette liste est vide pour l'instant.")}
            action={emptyAction}
          />
        </div>
      );
    }
    return (
      <>
        <div className="adm-dt-scroll" ref={scroller} style={maxHeight ? { maxHeight } : undefined}>
          <table aria-rowcount={count} aria-label={exportName || id}>
            <colgroup>
              {selectable && <col style={{ width: 34 }} />}
              {shownColumns.map(col => <col key={col.key} style={{ width: colWidth(col) }} />)}
              {(rowActions || renderDetail) && <col style={{ width: 40 }} />}
            </colgroup>
            <thead>
              <tr>
                {selectable && (
                  <th scope="col" className="adm-dt-check">
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      ref={el => { if (el) el.indeterminate = someSelected && !allPageSelected; }}
                      onChange={toggleAllOnPage}
                      aria-label="Sélectionner toutes les lignes de la page"
                      data-testid="table-select-page"
                    />
                  </th>
                )}
                {shownColumns.map(col => {
                  const sorted = sort?.key === col.key;
                  const Icon = sorted ? (sort.dir === "asc" ? ChevronUp : ChevronDown) : ArrowDownUp;
                  return (
                    <th
                      key={col.key}
                      scope="col"
                      className={col.align === "right" ? "is-num" : undefined}
                      aria-sort={sorted ? (sort.dir === "asc" ? "ascending" : "descending") : (col.sortable ? "none" : undefined)}
                    >
                      {col.sortable ? (
                        <button
                          type="button"
                          className={`adm-dt-sort${sorted ? " is-sorted" : ""}`}
                          onClick={() => toggleSort(col)}
                          data-testid={`sort-${col.key}`}
                        >
                          <span>{col.header}</span><Icon size={12} />
                        </button>
                      ) : <span>{col.header}</span>}
                      {col.resizable !== false && (
                        <button
                          type="button"
                          className="adm-dt-resize"
                          aria-label={`Redimensionner la colonne ${col.header}`}
                          onPointerDown={e => startResize(col, e)}
                          onKeyDown={e => {
                            if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                              e.preventDefault();
                              const delta = e.key === "ArrowLeft" ? -16 : 16;
                              setWidths(w => ({ ...w, [col.key]: Math.max(col.minWidth || 72, colWidth(col) + delta) }));
                            }
                          }}
                          data-testid={`resize-${col.key}`}
                        />
                      )}
                    </th>
                  );
                })}
                {(rowActions || renderDetail) && <th scope="col" className="adm-dt-end"><span className="adm-sr">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {windowed && firstIndex > 0 && (
                <tr className="adm-dt-spacer" aria-hidden="true"><td colSpan={shownColumns.length + 2} style={{ height: firstIndex * rowHeight }} /></tr>
              )}
              {rendered.map((row, i) => {
                const rowId = getRowId(row);
                const index = firstIndex + i;
                const isSelected = selected.has(rowId) || allMatching;
                return (
                  <tr
                    key={rowId}
                    aria-rowindex={index + 2}
                    aria-selected={selectable ? isSelected : undefined}
                    className={[
                      onRowClick || renderDetail ? "is-clickable" : "",
                      isSelected ? "is-selected" : "",
                      openRow && getRowId(openRow) === rowId ? "is-open" : "",
                    ].filter(Boolean).join(" ")}
                    onClick={() => openDetail(row)}
                    data-testid={`row-${rowId}`}
                  >
                    {selectable && (
                      <td className="adm-dt-check" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          onClick={e => toggleRow(rowId, index, e)}
                          aria-label={`Sélectionner la ligne ${index + 1}`}
                          data-testid={`select-${rowId}`}
                        />
                      </td>
                    )}
                    {shownColumns.map(col => (
                      <td key={col.key} className={col.align === "right" ? "is-num" : undefined} title={col.render ? undefined : String(rawValue(col, row) ?? "")}>
                        {col.render ? col.render(row) : rawValue(col, row)}
                      </td>
                    ))}
                    {(rowActions || renderDetail) && (
                      <td className="adm-dt-end" onClick={e => e.stopPropagation()}>
                        {rowActions
                          ? rowActions(row)
                          : (
                            <button
                              type="button"
                              className="adm-btn is-sm is-ghost is-icon"
                              onClick={() => openDetail(row)}
                              aria-label="Ouvrir la fiche"
                              data-testid={`open-${rowId}`}
                            >
                              <ChevronRight size={14} />
                            </button>
                          )}
                      </td>
                    )}
                  </tr>
                );
              })}
              {windowed && lastRendered < paged.length && (
                <tr className="adm-dt-spacer" aria-hidden="true"><td colSpan={shownColumns.length + 2} style={{ height: (paged.length - lastRendered) * rowHeight }} /></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile : chaque ligne devient une carte lisible, jamais une table compressée. */}
        <div className="adm-dt-cards">
          {paged.map(row => {
            const rowId = getRowId(row);
            const primary = shownColumns[0];
            const rest = shownColumns.slice(1, 5);
            return (
              <button type="button" key={rowId} className="adm-dt-card" onClick={() => openDetail(row)} data-testid={`card-${rowId}`}>
                <span className="adm-dt-card-top">
                  <strong>{primary ? (primary.render ? primary.render(row) : rawValue(primary, row)) : rowId}</strong>
                  <ChevronRight size={14} color="var(--adm-ink-4)" />
                </span>
                <span className="adm-dt-card-meta">
                  {rest.map(col => (
                    <span key={col.key}>{col.header}<b>{col.render ? col.render(row) : rawValue(col, row)}</b></span>
                  ))}
                </span>
              </button>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <>
      <div
        className={`adm-dt is-${density}${resizing ? " is-resizing" : ""} has-cards`}
        data-testid={`table-${id}`}
        aria-busy={loading || undefined}
      >
        <div className="adm-dt-bar">
          {searchable && (
            <div className="adm-field">
              <Search size={14} />
              <input
                ref={searchInputRef}
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                data-testid={`search-${id}`}
              />
            </div>
          )}
          <Filters filters={filters} values={filterValues} onChange={setFilterValues} onClear={() => setFilterValues({})} />
          <div className="adm-dt-bar-right">
            {toolbarExtra}
            <SavedViews storageKey={id} current={currentView} onApply={applyView} builtins={builtinViews} />
            <ColumnPicker
              columns={columns}
              visible={visible}
              onChange={setVisible}
              onReset={() => { setVisible(columns.filter(c => !c.hidden).map(c => c.key)); setWidths({}); }}
            />
            <Popover label="Densité" icon={Rows3} testid="density" title="Densité d'affichage" width={190}>
              {({ close }) => (
                <>
                  {[{ id: "compact", label: "Compacte" }, { id: "cosy", label: "Confortable" }].map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      className={`adm-menu-item${density === opt.id ? " is-active" : ""}`}
                      onClick={() => { setDensity(opt.id); close(); }}
                      data-testid={`density-${opt.id}`}
                    >
                      <span>{opt.label}</span>
                    </button>
                  ))}
                </>
              )}
            </Popover>
            <Popover label="Exporter" icon={Download} testid="export" title="Exporter" width={230}>
              {({ close }) => (
                <>
                  <div className="adm-menu-head">
                    <span>{someSelected && !allMatching ? `${selected.size} ligne(s) sélectionnée(s)` : `${count} ligne(s) filtrée(s)`}</span>
                  </div>
                  <button type="button" className="adm-menu-item" onClick={() => exportCsv(close)} data-testid="export-csv">
                    <span>CSV (point-virgule)</span>
                  </button>
                  <button type="button" className="adm-menu-item" onClick={() => exportJson(close)} data-testid="export-json">
                    <span>JSON</span>
                  </button>
                  <div className="adm-menu-empty">L'export reprend les colonnes affichées et le filtre courant. Il est journalisé.</div>
                </>
              )}
            </Popover>
          </div>
        </div>

        {body()}

        <div className="adm-dt-foot">
          <Pagination
            mode={paginationMode}
            page={page}
            pageSize={pageSize || count}
            total={count}
            pageSizes={pageSizes.filter(size => size <= MAX_PAGE_SIZE)}
            onPageChange={setPage}
            onPageSizeChange={pageSize === 0 ? undefined : setPageSize}
            hasNext={Boolean(cursor?.next)}
            hasPrev={Boolean(cursor?.prev)}
            onNext={() => onCursorChange?.({ direction: "next", cursor: cursor?.next })}
            onPrev={() => onCursorChange?.({ direction: "prev", cursor: cursor?.prev })}
            loading={loading}
          />
          <span style={{ fontSize: 11.5, color: "var(--adm-ink-4)" }}>
            {paginationMode === "cursor" ? "Pagination par curseur" : `Pagination stricte · ${MAX_PAGE_SIZE} lignes maximum par page`}
          </span>
        </div>
      </div>

      {selectable && (
        <BulkBar
          count={allMatching ? count : selected.size}
          total={count}
          allMatching={allMatching}
          onSelectAllMatching={() => setAllMatching(true)}
          onClear={clearSelection}
          actions={bulkActions.map(action => ({
            ...action,
            onRun: () => action.onRun({
              ids: allMatching ? null : Array.from(selected),
              allMatching,
              count: allMatching ? count : selected.size,
              query,
            }),
          }))}
        />
      )}

      {renderDetail && (
        <DetailDrawer
          open={Boolean(openRow)}
          onClose={() => setOpenRow(null)}
          title={openRow ? (detailTitle ? detailTitle(openRow) : String(getRowId(openRow))) : ""}
          subtitle={openRow && detailSubtitle ? detailSubtitle(openRow) : undefined}
          tabs={openRow && detailTabs ? detailTabs(openRow) : []}
          testid="table-detail"
        >
          {openRow ? renderDetail(openRow, { close: () => setOpenRow(null) }) : null}
        </DetailDrawer>
      )}
    </>
  );
}

export { MAX_PAGE_SIZE, toCsv };

// Icône laissée à disposition des colonnes d'actions des écrans.
export const RowMenuIcon = MoreHorizontal;
